import React, { FC, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetProjectByIdQuery,
  useSetHouseNumberMutation,
} from "../../api/projectsApi";
import { jumboMediaAPI } from "../../api/service";
import Form from "../../components/Form/Form";
import { ValidatorsE } from "../../components/Form/yupValidations";
import Loader from "../../components/Loader/Loader";
import { Span } from "../../components/Text/Text";
import { FileTypeE, LangE } from "../../models/enums";
import { FormFieldT, VersionT } from "../../models/models";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectCurrentLang, selectHouseNumber } from "../../store/selectors/appSelectors";
import { UNCLEAR_HOUSE_NUMBER_VALUE } from "../../utils/constants";
import { TOAST_DURATION } from "../../utils/helpers/constants";
import { convertNumToMinSec } from "../../utils/helpers/convertNumToMinSec";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import { fileTypeHebrewSO, fileTypeSO, selectValue } from "../../utils/helpers/selectOptions";
import usePopup from "../../utils/hooks/usePopup";

import styles from "./setHouseNumber.module.scss";

type SetHouseNumberBodyPropTypes = {
  AdId: string;
  projectId?: number;
};

const SetHouseNumberBody: FC<SetHouseNumberBodyPropTypes> = (props) => {
  const { AdId, projectId } = props;
  const lang = useAppSelector(selectCurrentLang);
  const houseNumber = useAppSelector(selectHouseNumber);
  const { pathname } = useLocation();
  const { data, isFetching } = useGetProjectByIdQuery({ id: projectId, detailed: true });
  const [dataVersion, setDataVersion] = useState<VersionT>();
  const [for2Spots, setFor2Spots] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (data) {
      const foundVersion = data.Versions.find((v) => v.AdId === AdId);
      setDataVersion(foundVersion);
      setFor2Spots(foundVersion.useFor2Spots);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      setDataVersion(undefined)
    }
  }, []);

  useEffect(() => {
    // if popup was opened from the campaign page
    if (pathname.includes("campaigns")) {
      dispatch(
        (jumboMediaAPI.endpoints as any).getProjectById.initiate({
          id: projectId,
        }) as any
      ).then(({ data }) => {
        const foundVersion = data.Versions.find((v) => v.AdId === AdId);
        setDataVersion(foundVersion);
      });
    }
  }, [pathname]);

  const fields: FormFieldT[] = !dataVersion
    ? []
    : [
        {
          label: "Use this version for two different spots",
          name: "twoSpots",
          defaultValue: dataVersion.useFor2Spots,
          onChange: (v) => setFor2Spots(v),
          type: "checkbox",
          style: !isMobileOrTablet() && { gridArea: "1 / 1 / 2 / 3" },
        },
        {
          label: "House number",
          defaultValue:
            houseNumber[AdId] !== UNCLEAR_HOUSE_NUMBER_VALUE
              ? houseNumber[AdId]
              : "",
          hintText: "Set house number",
          capitalLetters: true,
          validation: ValidatorsE.CAPITAL_LATIN_AND_NUMBERS,
          // style: !isMobileOrTablet()
          //   ? for2Spots
          //     ? { width: "calc(100% - 100px)" }
          //     : { gridArea: "2 / 1/ 2 / 3", width: "calc(50% - 110px)" }
          //   : undefined,
        },
        {
          label: "Content type",
          name: "FileType",
          selectOptions: lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO,
          defaultValue: selectValue(lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO, dataVersion.FileType),
          style: !isMobileOrTablet() && {
            gridColumn: 2,
            gridRow: 2,
            maxWidth: "200px",
          },
        },
        {
          label: for2Spots ? "Sponsorship house number" : null,
          // validation: for2Spots ? ValidatorsE.REQUIRED_STRING : null,
          validation: ValidatorsE.CAPITAL_LATIN_AND_NUMBERS,
          capitalLetters: true,
          name: "sponsershipHouseNumber",
          defaultValue: dataVersion.sponsershipHouseNumber,
          hintText: "Set house number",
          // style: { width: !isMobileOrTablet() && "calc(100% - 100px)" },
        },
        {
          label: for2Spots ? "Sponsorship content type" : null,
          name: "SecondHouseNumberType",
          selectOptions: lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO,
          defaultValue: selectValue(
            lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO,
            dataVersion.SecondHouseNumberType || FileTypeE.Sponsorship
          ),
          style: !isMobileOrTablet() && {
            gridColumn: 2,
            gridRow: 3,
            maxWidth: "200px",
          },
        },
      ];
  const [setHouseNumber] = useSetHouseNumberMutation();
  const { removePopup } = usePopup();

  useEffect(() => {
    const sendToFTPButton = document.getElementById("sendToFTPButton");
    if (sendToFTPButton) {
      sendToFTPButton.style.opacity = for2Spots ? "0" : "1";
      sendToFTPButton.style.pointerEvents = for2Spots ? "none" : "auto";
    }
  }, [for2Spots]);

  const validateFormData = (data: any) => {
    handleSubmit(data);
  };

  const handleSubmit = async (data: any) => {
    const body = {
      HouseNumber: data.houseNumber,
      sponsershipHouseNumber: data.sponsershipHouseNumber,
      NotSuitableForBroadcast: dataVersion.NotSuitableForBroadcast,
      NotSuitableForBroadcastComment:
        dataVersion.NotSuitableForBroadcastComment,
      NotSuitableForBroadcastReason: dataVersion.NotSuitableForBroadcastReason,
      NotSuitableForBroadcastType: dataVersion.NotSuitableForBroadcastType,
      ProjectType: "VideoAd",
      useFor2Spots: data.twoSpots,
      Id: dataVersion.Id,
      FileType: data.FileType?.value,
      SecondHouseNumberType: data.sponsershipHouseNumber
        ? data.SecondHouseNumberType?.value
        : 0,
    };

    try {
      const res = await setHouseNumber(body);
      toast("Successfully changed", {
        type: "success",
        autoClose: TOAST_DURATION,
      });
      removePopup();
    } catch (e: any) {
      console.log(e);
    }
  };

  return (dataVersion && !isFetching) ? (
    <>
      <div className={styles.versionInfo}>
        <p>
          <Span>AD-ID</Span>: {AdId}
        </p>
        <p>
          <Span>Version name</Span>: {dataVersion?.Name}
        </p>
        <p>
          <Span>Length: </Span>
          {convertNumToMinSec(dataVersion?.RequiredLength || 0)}
        </p>
      </div>
      <Form
        fields={fields}
        formID="set-house-number"
        onSubmit={validateFormData}
        className={styles.formWrapper}
      />
    </>
  ) : (
    <Loader />
  );
};

export default SetHouseNumberBody;
